import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, Copy, HandCoins, FileText, Building2, Trash2, Pencil, Archive, RotateCcw, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { formatTRY } from "@/lib/format";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DepositDialog, ContractDialog } from "@/components/UnitSaleFlow";
import UnitAttachments from "@/components/UnitAttachments";
import MoneyInput from "@/components/MoneyInput";

const STATUS_TONE = {
  bos: "bg-secondary text-muted-foreground",
  satildi: "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))]",
  rezerve: "bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))]",
};
const STATUS_LABEL = { bos: "Boş", satildi: "Satıldı", rezerve: "Rezerve" };

export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPerm } = useAuth();
  const [project, setProject] = useState(null);
  const [units, setUnits] = useState([]);
  const [archivedUnits, setArchivedUnits] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [editingUnitId, setEditingUnitId] = useState(null);
  const [depositOpen, setDepositOpen] = useState(false);
  const [contractOpen, setContractOpen] = useState(false);
  const [copyOpen, setCopyOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  const [unitForm, setUnitForm] = useState({
    block: "", floor: "", unit_no: "", area: 0, rooms: "", price: 0, status: "bos",
  });
  const [copyForm, setCopyForm] = useState({ count: 4, start_unit_no: 101, block: "", floor: "" });

  const load = async () => {
    const [{ data: p }, { data: u }, { data: au }] = await Promise.all([
      api.get(`/projects/${id}`),
      api.get(`/units?project_id=${id}&archived=false`),
      api.get(`/units?project_id=${id}&archived=true`),
    ]);
    setProject(p);
    setUnits(u);
    setArchivedUnits(au);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [id]);

  const openAddUnit = () => {
    setEditingUnitId(null);
    setUnitForm({ block: "", floor: "", unit_no: "", area: 0, rooms: "", price: 0, status: "bos" });
    setAddOpen(true);
  };

  const openEditUnit = (u) => {
    setEditingUnitId(u.id);
    setUnitForm({
      block: u.block || "", floor: u.floor || "", unit_no: u.unit_no || "",
      area: u.area || 0, rooms: u.rooms || "", price: u.price || 0,
      status: u.status || "bos",
    });
    setAddOpen(true);
  };

  const saveUnit = async () => {
    try {
      const payload = { ...unitForm, project_id: id, area: Number(unitForm.area), price: Number(unitForm.price) };
      if (editingUnitId) {
        await api.patch(`/units/${editingUnitId}`, payload);
        toast.success("Daire güncellendi");
      } else {
        await api.post("/units", payload);
        toast.success("Daire eklendi");
      }
      setAddOpen(false);
      setEditingUnitId(null);
      setUnitForm({ block: "", floor: "", unit_no: "", area: 0, rooms: "", price: 0, status: "bos" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openDeposit = (u) => { setSelected(u); setDepositOpen(true); };
  const openContract = (u) => { setSelected(u); setContractOpen(true); };

  const openCopy = (u) => {
    setSelected(u);
    setCopyForm({ count: 4, start_unit_no: parseInt(u.unit_no.replace(/\D/g, ""), 10) + 1 || 101, block: u.block, floor: u.floor });
    setCopyOpen(true);
  };

  const doCopy = async () => {
    try {
      const { data } = await api.post("/units/bulk-copy", {
        template_unit_id: selected.id,
        count: Number(copyForm.count),
        start_unit_no: Number(copyForm.start_unit_no),
        block: copyForm.block || null,
        floor: copyForm.floor || null,
      });
      toast.success(`${data.count} daire oluşturuldu`);
      setCopyOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const removeUnit = async (u) => {
    if (!window.confirm(`${u.unit_no} nolu daireyi arşive taşımak istediğinize emin misiniz?`)) return;
    await api.delete(`/units/${u.id}`);
    toast.success("Daire arşive taşındı");
    load();
  };

  const restoreUnit = async (u) => {
    await api.post(`/units/${u.id}/restore`);
    toast.success("Daire geri getirildi");
    load();
  };

  const purgeUnit = async (u) => {
    if (!window.confirm(`${u.unit_no} nolu daireyi KALICI olarak silmek istediğinize emin misiniz? Bu işlem geri alınamaz.`)) return;
    await api.delete(`/units/${u.id}/purge`);
    toast.success("Daire kalıcı olarak silindi");
    load();
  };

  if (!project) return <div className="text-muted-foreground text-sm">Yükleniyor…</div>;

  const soldCount = units.filter((u) => u.status === "satildi").length;
  const totalRevenue = units.reduce((s, u) => s + (u.sale_price || 0), 0);

  return (
    <div className="space-y-6" data-testid="project-detail-page">
      <button
        onClick={() => navigate("/projects")}
        className="text-xs uppercase tracking-widest text-muted-foreground hover:text-foreground font-semibold flex items-center gap-1"
      >
        <ArrowLeft className="w-3 h-3" /> Projeler
      </button>

      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-md bg-secondary flex items-center justify-center">
              <Building2 className="w-6 h-6 text-brand" />
            </div>
            <div>
              <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">{project.name}</h1>
              <div className="text-sm text-muted-foreground">{project.address}</div>
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            data-testid="project-export-btn"
            onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/reports/project/${id}/excel`, "_blank", "noopener")}
          >
            <FileSpreadsheet className="w-4 h-4 mr-2" /> Excel Rapor
          </Button>
          {hasPerm("units.create") && (
            <Button data-testid="add-unit-btn" onClick={openAddUnit}>
              <Plus className="w-4 h-4 mr-2" /> Daire Ekle
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="p-4 shadow-none rounded-md">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Toplam Daire</div>
          <div className="font-heading text-2xl font-bold mt-2 tabular-nums">{units.length}</div>
        </Card>
        <Card className="p-4 shadow-none rounded-md">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Satılan</div>
          <div className="font-heading text-2xl font-bold mt-2 tabular-nums">{soldCount}</div>
        </Card>
        <Card className="p-4 shadow-none rounded-md">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Satış Geliri</div>
          <div className="font-heading text-2xl font-bold mt-2 tabular-nums">{formatTRY(totalRevenue)}</div>
        </Card>
        <Card className="p-4 shadow-none rounded-md">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Bütçe</div>
          <div className="font-heading text-2xl font-bold mt-2 tabular-nums">{formatTRY(project.budget)}</div>
        </Card>
      </div>

      {(project.mahalle || project.ada || project.parsel) && (
        <Card className="p-5 shadow-none rounded-md border-l-4 border-l-brand" data-testid="project-tapu-card">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-3">
            Tapu Bilgileri
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <div className="text-xs text-muted-foreground mb-1">Mahalle</div>
              <div className="font-heading text-lg font-semibold tracking-tight">{project.mahalle || "-"}</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground mb-1">Ada</div>
              <div className="font-heading text-lg font-semibold tracking-tight tabular-nums">{project.ada || "-"}</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground mb-1">Parsel</div>
              <div className="font-heading text-lg font-semibold tracking-tight tabular-nums">{project.parsel || "-"}</div>
            </div>
          </div>
        </Card>
      )}

      <Card className="shadow-none rounded-md overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-secondary/50">
                <TableHead>Blok</TableHead>
                <TableHead>Kat</TableHead>
                <TableHead>No</TableHead>
                <TableHead>Oda</TableHead>
                <TableHead className="text-right">m²</TableHead>
                <TableHead className="text-right">Fiyat</TableHead>
                <TableHead>Durum</TableHead>
                <TableHead>Alıcı</TableHead>
                <TableHead className="text-right">İşlem</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {units.map((u) => (
                <TableRow key={u.id} data-testid={`unit-row-${u.id}`} className="hover:bg-secondary/40">
                  <TableCell className="font-medium">{u.block || "-"}</TableCell>
                  <TableCell>{u.floor || "-"}</TableCell>
                  <TableCell className="font-semibold">{u.unit_no}</TableCell>
                  <TableCell>{u.rooms || "-"}</TableCell>
                  <TableCell className="text-right tabular-nums">{u.area}</TableCell>
                  <TableCell className="text-right tabular-nums">{formatTRY(u.price)}</TableCell>
                  <TableCell>
                    <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded font-semibold ${STATUS_TONE[u.status]}`}>
                      {STATUS_LABEL[u.status]}
                    </span>
                  </TableCell>
                  <TableCell className="text-muted-foreground">{u.buyer_name || "-"}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      {u.status === "bos" && hasPerm("units.edit") && (
                        <Button size="sm" data-testid={`deposit-btn-${u.id}`} onClick={() => openDeposit(u)} variant="outline" className="h-8">
                          <HandCoins className="w-3 h-3 mr-1" /> Kapora Al
                        </Button>
                      )}
                      {u.status === "rezerve" && hasPerm("units.edit") && (
                        <Button size="sm" data-testid={`contract-btn-${u.id}`} onClick={() => openContract(u)} className="h-8">
                          <FileText className="w-3 h-3 mr-1" /> Sözleşme Hazırla
                        </Button>
                      )}
                      {u.status === "satildi" && hasPerm("units.view") && (
                        <Button size="sm" data-testid={`view-contract-btn-${u.id}`} onClick={() => openContract(u)} variant="ghost" className="h-8">
                          <FileText className="w-3 h-3 mr-1" /> Sözleşme
                        </Button>
                      )}
                      {hasPerm("units.edit") && (
                        <Button size="sm" variant="outline" onClick={() => openEditUnit(u)} data-testid={`edit-unit-btn-${u.id}`} className="h-8 px-2 border-brand/40 text-brand hover:bg-brand/10" title="Düzenle">
                          <Pencil className="w-3 h-3 mr-1" /> Düzenle
                        </Button>
                      )}
                      {hasPerm("units.create") && (
                        <Button size="sm" variant="ghost" onClick={() => openCopy(u)} data-testid={`copy-btn-${u.id}`} className="h-8 w-8 p-0" title="Çoğalt">
                          <Copy className="w-3 h-3" />
                        </Button>
                      )}
                      {hasPerm("units.delete") && u.status !== "satildi" && (
                        <Button size="sm" variant="ghost" onClick={() => removeUnit(u)} className="h-8 w-8 p-0 text-destructive">
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {units.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} className="text-center text-muted-foreground py-8">Bu projede daire yok.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </Card>

      {/* Arşivdeki (silinen) daireler */}
      {archivedUnits.length > 0 && (
        <Card className="shadow-none rounded-md overflow-hidden border-dashed" data-testid="archived-units-card">
          <button
            type="button"
            onClick={() => setShowArchived((s) => !s)}
            className="w-full flex items-center justify-between px-4 py-3 bg-secondary/40 hover:bg-secondary/60 transition-colors"
            data-testid="toggle-archived-units"
          >
            <div className="flex items-center gap-2 text-sm font-semibold">
              <Archive className="w-4 h-4 text-muted-foreground" />
              Silinen Daireler
              <span className="text-xs text-muted-foreground font-normal">({archivedUnits.length})</span>
            </div>
            <span className="text-xs text-muted-foreground">{showArchived ? "Gizle" : "Göster"}</span>
          </button>
          {showArchived && (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-secondary/30">
                    <TableHead>Blok</TableHead>
                    <TableHead>Kat</TableHead>
                    <TableHead>No</TableHead>
                    <TableHead>Oda</TableHead>
                    <TableHead className="text-right">m²</TableHead>
                    <TableHead className="text-right">Fiyat</TableHead>
                    <TableHead>Durum</TableHead>
                    <TableHead className="text-right w-48">İşlem</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {archivedUnits.map((u) => (
                    <TableRow key={u.id} data-testid={`archived-unit-row-${u.id}`} className="opacity-70">
                      <TableCell>{u.block || "-"}</TableCell>
                      <TableCell>{u.floor || "-"}</TableCell>
                      <TableCell className="font-semibold">{u.unit_no}</TableCell>
                      <TableCell>{u.rooms || "-"}</TableCell>
                      <TableCell className="text-right tabular-nums">{u.area}</TableCell>
                      <TableCell className="text-right tabular-nums">{formatTRY(u.price)}</TableCell>
                      <TableCell>
                        <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded font-semibold ${STATUS_TONE[u.status]}`}>
                          {STATUS_LABEL[u.status]}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          {hasPerm("units.edit") && (
                            <Button size="sm" variant="outline" onClick={() => restoreUnit(u)} data-testid={`restore-unit-${u.id}`} className="h-8">
                              <RotateCcw className="w-3 h-3 mr-1" /> Geri Getir
                            </Button>
                          )}
                          {hasPerm("units.delete") && (
                            <Button size="sm" variant="ghost" onClick={() => purgeUnit(u)} data-testid={`purge-unit-${u.id}`} className="h-8 w-8 p-0 text-destructive" title="Kalıcı Sil">
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </Card>
      )}

      {/* Proje Belgeleri */}
      <UnitAttachments project={project} canEdit={hasPerm("projects.edit")} defaultLabel="Proje Belgesi" />

      {/* Add Unit Dialog */}
      <Dialog open={addOpen} onOpenChange={(o) => { setAddOpen(o); if (!o) setEditingUnitId(null); }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle className="font-heading">{editingUnitId ? "Daire Düzenle" : "Daire Ekle"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2"><Label>Blok</Label><Input data-testid="unit-block" value={unitForm.block} onChange={(e) => setUnitForm({ ...unitForm, block: e.target.value })} /></div>
            <div className="space-y-2"><Label>Kat</Label><Input value={unitForm.floor} onChange={(e) => setUnitForm({ ...unitForm, floor: e.target.value })} /></div>
            <div className="space-y-2"><Label>Daire No</Label><Input data-testid="unit-no" value={unitForm.unit_no} onChange={(e) => setUnitForm({ ...unitForm, unit_no: e.target.value })} /></div>
            <div className="space-y-2"><Label>Oda</Label><Input value={unitForm.rooms} onChange={(e) => setUnitForm({ ...unitForm, rooms: e.target.value })} placeholder="3+1" /></div>
            <div className="space-y-2"><Label>m²</Label><Input type="number" value={unitForm.area} onChange={(e) => setUnitForm({ ...unitForm, area: e.target.value })} /></div>
            <div className="space-y-2"><Label>Fiyat</Label><MoneyInput data-testid="unit-price" value={unitForm.price} onChange={(n) => setUnitForm({ ...unitForm, price: n })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setAddOpen(false); setEditingUnitId(null); }}>İptal</Button>
            <Button data-testid="unit-save" onClick={saveUnit} disabled={!unitForm.unit_no}>{editingUnitId ? "Kaydet" : "Ekle"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sale replaced by Kapora → Sözleşme flow */}
      <DepositDialog open={depositOpen} onOpenChange={setDepositOpen} unit={selected} onDone={load} />
      <ContractDialog open={contractOpen} onOpenChange={setContractOpen} unit={selected} onDone={load} />

      {/* Copy Dialog */}
      <Dialog open={copyOpen} onOpenChange={setCopyOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-heading">Daireyi Çoğalt</DialogTitle></DialogHeader>
          <div className="text-xs text-muted-foreground mb-3">Şablon: <span className="font-medium">{selected?.unit_no}</span> — Bu daire baz alınarak N adet yeni daire oluşturulur.</div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2"><Label>Adet</Label><Input data-testid="copy-count" type="number" value={copyForm.count} onChange={(e) => setCopyForm({ ...copyForm, count: e.target.value })} /></div>
            <div className="space-y-2"><Label>Başlangıç No</Label><Input data-testid="copy-start" type="number" value={copyForm.start_unit_no} onChange={(e) => setCopyForm({ ...copyForm, start_unit_no: e.target.value })} /></div>
            <div className="space-y-2"><Label>Blok</Label><Input value={copyForm.block} onChange={(e) => setCopyForm({ ...copyForm, block: e.target.value })} /></div>
            <div className="space-y-2"><Label>Kat</Label><Input value={copyForm.floor} onChange={(e) => setCopyForm({ ...copyForm, floor: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCopyOpen(false)}>İptal</Button>
            <Button data-testid="copy-confirm" onClick={doCopy}>Çoğalt</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
